import type { ReactNode } from "react";
import { Wrench, Fuel, JapaneseYen, BarChart3 } from "lucide-react";

export function QuickActions({
  onInspection,
  onExpense,
  onExpensesList,
  onRefueling,
  inspectionDisabledReason,
  refuelingDisabledReason,
  expenseDisabledReason,
}: {
  onInspection: () => void;
  onExpense: () => void;
  onExpensesList: () => void;
  onRefueling: () => void;
  /**
   * Issue #401: 点検・給油・経費は日報にぶら下がり、日報には出勤が必要。
   * 出勤前 (および点検は退勤後) は理由を渡して導線を無効化する。null/undefined = 利用可。
   * 経費履歴は閲覧のみのため常に利用可。
   */
  inspectionDisabledReason?: string | null;
  refuelingDisabledReason?: string | null;
  expenseDisabledReason?: string | null;
}) {
  // 「明日」タブでは呼び出し側 (App.tsx) がこのコンポーネント自体を描画しないため、
  // ここでは常に全ての作業導線を出す (Issue #338)。
  const items: {
    icon: ReactNode;
    label: string;
    onClick: () => void;
    disabledReason?: string | null;
  }[] = [
    {
      icon: <Wrench size={20} />,
      label: "車両点検",
      onClick: onInspection,
      disabledReason: inspectionDisabledReason,
    },
    {
      icon: <Fuel size={20} />,
      label: "給油",
      onClick: onRefueling,
      disabledReason: refuelingDisabledReason,
    },
    {
      icon: <JapaneseYen size={20} />,
      label: "経費追加",
      onClick: onExpense,
      disabledReason: expenseDisabledReason,
    },
    { icon: <BarChart3 size={20} />, label: "経費履歴", onClick: onExpensesList },
  ];
  // 同じ理由はまとめて 1 行で表示する (出勤前は 3 導線とも同一文言のため)。
  const reasons = Array.from(
    new Set(items.map((x) => x.disabledReason).filter((r): r is string => !!r)),
  );
  return (
    <div className="m-2.5">
      <div className="grid grid-cols-4 gap-1.5">
        {items.map((x) => (
          <button
            key={x.label}
            type="button"
            onClick={x.onClick}
            disabled={!!x.disabledReason}
            title={x.disabledReason ?? undefined}
            className="flex cursor-pointer flex-col items-center gap-1 rounded-lg border border-[var(--rule-soft)] bg-[var(--bg-raised)] py-2.5 text-[length:var(--fs-sm)] font-medium text-[var(--ink-2)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <span className="flex items-center">{x.icon}</span>
            {x.label}
          </button>
        ))}
      </div>
      {reasons.map((reason) => (
        <p key={reason} className="mt-1.5 text-[length:var(--fs-xs)] text-[var(--ink-3)]">
          {reason}
        </p>
      ))}
    </div>
  );
}
